import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { SessionService } from 'src/app/auth/session.service';
import { TwilioError } from 'src/app/components/chat/services/twilio/twilio.interfaces';
import { environment } from 'src/environments/environment';
import { chunkErrorMessages } from './chuck-error-messages';
import { isChunkError, isTwilioPromiseRejectionError } from './sentry-error-helpers';
import { TwilioPromiseErrorMediator } from './twilio-promise-error-mediator';

Sentry.init({
  release: (environment.projectName as string) + '@' + environment.version,
  dsn: 'https://d2ebdd11b28e4a4d913584ca51a9f3f0@o372018.ingest.sentry.io/6677203',
  environment: environment.name,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  allowUrls: [/(?:[a-z]+\.)?1life2connect\.com/],
  ignoreErrors: [
    'Internal Server error',
    'Session is no longer valid',
    'Check the Internet connection',
    'Non-Error exception captured',
    'Script error.',
    'ResizeObserver loop limit exceeded',
  ],
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  private sessionService = inject(SessionService);
  private twilioPromiseErrorMediator = inject(TwilioPromiseErrorMediator);

  public constructor() {
    this.twilioPromiseErrorMediator.twilioHandledErrorOccured.subscribe((error: unknown) =>
      this.reportTwilioError(error),
    );
  }

  public extractError(error: string | Error | unknown) {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error && (error as { ngOriginalError: Error }).ngOriginalError) {
      error = (error as { ngOriginalError: Error }).ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Skip if there's no error, and let user decide what to do with it.
    return null;
  }

  public handleError(error: Error) {
    const extractedError = this.extractError(error) || 'Handled unknown error';
    if (isTwilioPromiseRejectionError(error as TwilioError)) {
      this.twilioPromiseErrorMediator.onTwilioUnhandledPromiseRejectionError(error);
      return;
    }

    // Capture handled exception and send it to Sentry.
    const eventId: string = Sentry.captureException(extractedError);

    if (isChunkError(error, chunkErrorMessages)) {
      window.location.reload();
    }

    let email = '';
    let name = '';
    if (this.sessionService.user) {
      email = this.sessionService.user.profile.email || '';
      name = this.sessionService.user.displayed_name || '';
      Sentry.setUser({
        email,
        username: name,
      });
    }

    // When in development mode, log the error to console for immediate feedback.
    if (!environment.production) {
      console.table(extractedError);
    }

    // Optionally show user dialog to provide details on what happened.
    Sentry.showReportDialog({
      eventId,
      user: {
        email,
        name,
      },
    });
  }

  private reportTwilioError(error: unknown): void {
    // Capture handled exception and send it to Sentry.
    Sentry.captureException(this.extractError(error) || 'Handled unknown error');
  }
}
